import { AuthOAuthButton } from './AuthOAuthButton';
import type { OAuthProvidersSchema } from '@insforge/shared-schemas';
import { OAuthProvidersContainer } from './styled';

export interface AuthOAuthProvidersProps {
  providers: OAuthProvidersSchema[];
  onClick: (provider: OAuthProvidersSchema) => void;
  disabled?: boolean;
  loading?: OAuthProvidersSchema | null;
}

/**
 * Smart OAuth provider grid with adaptive layout.
 *
 * Automatically adjusts layout based on provider count:
 * - 1 provider: Full-width
 * - 2 or 4 providers: Two columns
 * - 3+ providers: Three columns
 */
export function AuthOAuthProviders({
  providers,
  onClick,
  disabled,
  loading,
}: AuthOAuthProvidersProps) {
  if (!providers || providers.length === 0) {
    return null;
  }

  const count = providers.length;

  // Determine display mode based on count
  const getDisplayMode = () => {
    if (count === 1) {
      return 'full';
    }
    if (count === 2 || count === 4) {
      return 'short';
    }
    return 'icon';
  };

  // Calculate grid column style for each button
  // Grid is always 6 columns, but buttons span different widths based on total count
  const getGridColumnStyle = (index: number): React.CSSProperties => {
    // 1 button: span all 6 columns
    if (count === 1) {
      return { gridColumn: 'span 6' };
    }

    // 2 buttons: each spans 3 columns
    if (count === 2) {
      return { gridColumn: 'span 3' };
    }

    // 3 buttons: each spans 2 columns
    if (count === 3) {
      return { gridColumn: 'span 2 / span 2' };
    }

    // 4 buttons: 2 rows, each button spans 3 columns
    if (count === 4) {
      return { gridColumn: 'span 3' };
    }

    // 5+ buttons: each spans 2 columns, with last row centered if needed
    const totalRows = Math.ceil(count / 3);
    const lastRowStartIndex = (totalRows - 1) * 3;
    const isInLastRow = index >= lastRowStartIndex;

    if (!isInLastRow) {
      // Not in last row, use default span 2
      return { gridColumn: 'span 2 / span 2' };
    }

    // Calculate position in last row (0-based)
    const positionInLastRow = index - lastRowStartIndex;
    const itemsInLastRow = count - lastRowStartIndex;

    if (itemsInLastRow === 1) {
      // Last row has 1 item: center it at columns 3-4 (span 2 in middle)
      return { gridColumn: '3 / 5' };
    } else if (itemsInLastRow === 2) {
      // Last row has 2 items: center them symmetrically
      if (positionInLastRow === 0) {
        return { gridColumn: '2 / 4' }; // First button: cols 2-3
      } else {
        return { gridColumn: '4 / 6' }; // Second button: cols 4-5
      }
    } else {
      // Last row has 3 items: normal span 2
      return { gridColumn: 'span 2 / span 2' };
    }
  };

  return (
    <OAuthProvidersContainer
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '0.75rem',
      }}
    >
      {providers.map((provider, index) => (
        <AuthOAuthButton
          key={provider}
          provider={provider}
          onClick={onClick}
          disabled={disabled}
          loading={loading === provider}
          displayMode={getDisplayMode()}
          style={getGridColumnStyle(index)}
        />
      ))}
    </OAuthProvidersContainer>
  );
}
